import dayjs from 'dayjs';
import { Button, Spin } from 'antd';
import { Link } from 'react-router-dom';
import { getMeetingCardState } from 'commons/libs/meeting_utils';
import { Document } from 'commons/types/documentsTypes';
import {
  MEETING_CARD_STATUSES,
  Meeting,
  MeetingCardStatus,
  MeetingType,
} from 'commons/types/meetingsTypes';
import { Patient } from 'commons/types/patientsTypes';
import { useEffect } from 'react';

interface NextStepCardProps {
  patient: Patient | undefined;
  documents: Document[];
  loading: boolean;
  meeting: Meeting | undefined;
  updateStatus: (
    status: MeetingCardStatus,
    type: MeetingType | undefined,
  ) => void;
}

interface NextStepItemCardprops {
  title: string;
  content: string;
  action?: {
    to: string;
    text: string;
  };
}
const CONTENTS: Record<MeetingCardStatus, NextStepItemCardprops> = {
  OK: {
    title: '',
    content: '',
  },
  TO_JUSTIFY_CMU: {
    title: 'Transmettre mon justificatifs',
    content:
      "Votre RDV est planifié et nous avons reçu votre ordonnance. Il nous manque votre justificatif de CMU ou d'ALD.",
    action: {
      to: '/documents',
      text: 'Transmettre le document CMU/ALD',
    },
  },
  TO_JUSTIFY_PRESCRIPTION: {
    title: 'Transmettre mon ordonnance',
    content:
      'Votre RDV avec le médecin est planifié, mais il manque votre ordonnance (bilan de sommeil ou polygraphie ventilatoire). Elle est indispensable pour votre remboursement.',
    action: {
      to: '/documents',
      text: 'Transmettre mon ordonnance',
    },
  },
  TO_SCHEDULE_1: {
    title:
      'Réserver un entretien téléphonique de 15 minutes avec un conseiller médical',
    content: `Il saura vous aider à identifier vos symptômes et le parcours médical le plus adapté. Vous serez contacté(e) directement sur votre numéro de portable.`,
    action: {
      to: '/schedule',
      text: 'Planifier mon rendez-vous',
    },
  },
  TO_SCHEDULE_2: {
    title: 'Réserver un RDV médical directement en clinique',
    content:
      "Le RDV dure environ 20 minutes. L'infirmière vous remettra un appareil médical de diagnostic d’apnée du sommeil. Le médecin analysera les résultats quelques jours après. Ce RDV est remboursé par la sécurité sociale.",
    action: {
      to: '/schedule',
      text: 'Planifier mon rendez-vous',
    },
  },
  TO_RESCHEDULE_2: {
    title: 'Examen du sommeil non exploitable. Reprendre un RDV de diagnostic',
    content:
      "Malheureusement, vos données de sommeil ne sont pas exploitables. Vous pouvez reprendre un RDV avec l'infirmière.",
    action: {
      to: '/schedule',
      text: 'Planifier mon rendez-vous',
    },
  },
  WAITING_FOR_VALIDATION_RDV_3: {
    title: "Vos résultats sont en cours d'analyse.",
    content:
      "Si vous n'avez pas encore fourni votre ordonnance, vous pouvez le faire :",
    action: {
      to: '/documents',
      text: 'Transmettre mon ordonnance',
    },
  },

  TO_SCHEDULE_3: {
    title: 'Réserver un créneau de consultation avec le médecin du sommeil',
    content:
      'Prenez rendez-vous avec le médecin spécialiste pour la restitution de vos résultats',
    action: {
      to: '/schedule',
      text: 'Planifier mon rendez-vous',
    },
  },
  TO_SEND_PSAD: {
    title: 'Appareillage PPC',
    content:
      'Le médecin vous a prescrit un appareillage PPC. Valider la demande ci-dessous pour que le technicien assure votre appareillage dans les jours à venir.',
    action: {
      to: '__EQUIPEMENT_HAPNI__',
      text: 'Valider la demande',
    },
  },
  TO_SCHEDULE_PPC: {
    title: 'Renouveller mon ordonnance après 4 mois',
    content:
      'Pour renouveller votre ordonnance de PPC, vous pouvez réserver un créneau avec votre médecin.',
    action: {
      to: '/schedule',
      text: 'Prendre mon RDV de renouvellement',
    },
  },
  TO_SCHEDULE_PSG: {
    title: 'Examen PSG prescrit',
    content:
      'Le médecin vous a prescrit une polysomnographie. Nous vous recommandons des centres possibles via le lien ci-dessous.',
    action: {
      to: '__PSG_CENTERS__',
      text: 'Solliciter une PSG',
    },
  },
  TO_SCHEDULE_SWITCH: {
    title: "Planifier un appel avec l'infirmière",
    content:
      "Vous souhaitez changer de prestataire ? Vous avez des questions sur votre traitement ? Réservez un créneau d'appel avec notre infirmière qui se chargera de vous orienter.",
    action: {
      to: '/schedule',
      text: 'Planifier mon rendez-vous',
    },
  },
  CANCELED_TO_SCHEDULE_1: {
    title:
      'Réserver un entretien téléphonique de 15 minutes avec un conseiller médical',
    content:
      'Il saura vous aider à identifier vos symptômes et le parcours médical le plus adapté. Vous serez contacté(e) directement sur votre numéro de portable.',
    action: {
      to: '/schedule',
      text: 'Planifier mon RDV',
    },
  },
  CANCELED_TO_SCHEDULE_2: {
    title: 'Dernier rendez-vous non-honoré',
    content:
      "Vous n'avez pas honoré le RDV qui était prévu avec l'infirmière. Nous avons gardé votre dossier en attente. Vous pouvez reprendre un RDV directement.",
    action: {
      to: '/schedule',
      text: 'Planifier mon RDV',
    },
  },
  CANCELED_TO_SCHEDULE_3: {
    title: 'Rendez-vous avec le médecin non-honoré : replanifier.',
    content:
      "Vous n'avez pas honoré le rendez-vous qui était prévu avec le médecin. Il a déjà analysé votre dossier. Planifier une consultation pour qu'il vous partage son diagnostic.",
    action: {
      to: '/schedule',
      text: 'Réserver une consultation',
    },
  },
  CANCELED_TO_SCHEDULE_PPC: {
    title: 'Rendez-vous manqué ; reprendre un RDV',
    content:
      "Vous n'avez pas honoré le rendez-vous qui était prévu avec le médecin.",
    action: {
      to: '/schedule',
      text: 'Prendre mon RDV de renouvellement',
    },
  },
  CANCELED_TO_SCHEDULE_SWITCH: {
    title: "Rendez-vous manqué; reprendre un RDV avec l'infirmière",
    content:
      "Vous n'avez pas honoré le rendez-vous qui était prévu avec l'infirmière. Vous pouvez facilement reprendre RDV.",
    action: {
      to: '/schedule',
      text: 'Planifier mon RDV',
    },
  },
  FINISH: {
    title: 'Votre parcours avec Hapni est terminé !',
    content: '',
  },
};
const OK_CONTENTS: Record<MeetingType, NextStepItemCardprops> = {
  None: {
    title: '',
    content: '',
  },
  RDV_1: {
    title: 'Un conseiller médical va vous contacter',
    content:
      'Il saura vous aider à identifier vos symptômes et le parcours médical le plus adapté. Vous serez contacté(e) directement sur votre numéro de portable',
  },
  RDV_2: {
    title: 'Vous avez RDV pour votre diagnostic d’apnée du sommeil',
    content: `Le RDV dure environ 20 minutes. L'infirmière vous remettra un appareil médical de diagnostic d’apnée du sommeil. Le médecin analysera les résultats quelques jours après. Ce RDV est remboursé par la sécurité sociale.`,
  },
  RDV_3: {
    title: 'Rendez-vous avec le médecin à venir',
    content: 'Votre RDV est planifié et votre dossier est complet.',
  },
  RDV_PPC: {
    title: 'Rendez-vous avec le médecin à venir',
    content: 'Votre rendez de renouvellement est planifié.',
  },
  RDV_SWITCH: {
    title: 'Un infirmier spécialisé va vous contacter par téléphone',
    content:
      'Vous souhaitez faire le point sur votre traitement et comprendre les différentes solutions possibles.',
  },
};
export default function NextStepCard({
  patient,
  meeting,
  documents,
  loading,
  updateStatus,
}: NextStepCardProps) {
  const status = getMeetingCardState(
    meeting,
    documents,
    patient?.metadata?.status,
    patient?.metadata?.skipToRDV2,
    patient?.is_psad,
  );
  let body = CONTENTS[status];
  if (status === MEETING_CARD_STATUSES.OK && meeting?.type) {
    body = OK_CONTENTS[meeting?.type];
  }

  useEffect(() => {
    if (patient && !loading) {
      updateStatus(status, meeting?.type);
    }
  }, [loading, meeting?.type, patient, status, updateStatus]);

  if (!patient || loading) {
    return <Spin spinning />;
  }

  return (
    <div className='flex flex-col items-center w-full bg-hapni-gradient text-white px-6 rounded-2xl text-center text-sm'>
      <h2 className='my-4'>PROCHAINE ETAPE</h2>
      <p className='font-semibold text-base'>{body.title}</p>
      <p className='my-2 mb-4'>
        {body.content.replace(
          '__DELETE_HOUR__',
          `${dayjs().add(2, 'hour').format('HH')}h`,
        )}
      </p>
      {body.action && (
        <Link to={replaceTemplatesString(patient, meeting, body.action.to)}>
          <Button
            size='large'
            type='primary'
            className='my-4'
          >
            {body.action.text}
          </Button>
        </Link>
      )}
    </div>
  );
}

function replaceTemplatesString(
  patient: any | undefined,
  meeting: Meeting | undefined,
  to: string,
) {
  return to
    .replace('__EQUIPEMENT_HAPNI__', `https://hapni.fr/equipement/`)
    .replace(
      '__PSG_CENTERS__',
      'https://share-eu1.hsforms.com/1_WfcUDL0RmG68yhpecamDQfurss',
    );
}
